import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  User,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
}

export function AIPage(): React.ReactElement {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['ai-conversations'],
    queryFn: async () => {
      const res = await api.get<{ id: string; title: string; createdAt: string }[]>('/ai/conversations');
      return res.success ? (res.data ?? []) : [];
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input,
      createdAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);

    // Add placeholder assistant message
    const assistantId = crypto.randomUUID();
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    }]);

    try {
      const res = await api.post<{ response: string; conversationId: string }>('/ai/chat', {
        message: input,
        conversationId,
      });

      if (res.success && res.data) {
        setConversationId(res.data.conversationId);
        // Update the assistant message with the response
        setMessages(prev => prev.map(m =>
          m.id === assistantId ? { ...m, content: res.data!.response } : m
        ));
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Sorry, I encountered an error processing your request.' }
          : m
      ));
      toast.error(err instanceof Error ? err.message : 'AI request failed');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4">
      {/* Conversation Sidebar */}
      <div className="hidden w-64 shrink-0 lg:block">
        <Card className="h-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Bot className="h-4 w-4" /> Conversations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                setConversationId(null);
                setMessages([]);
              }}
            >
              <Sparkles className="mr-2 h-3 w-3" /> New Chat
            </Button>
            {isLoading ? (
              <div className="space-y-2 mt-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <div className="mt-2 space-y-1">
                {conversations?.map((conv) => (
                  <button
                    key={conv.id}
                    className={cn(
                      'w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                      conversationId === conv.id && 'bg-muted',
                    )}
                    onClick={() => setConversationId(conv.id)}
                  >
                    <p className="truncate">{conv.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(conv.createdAt).toLocaleDateString('en-AU')}
                    </p>
                  </button>
                ))}
                {conversations?.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No conversations yet
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Chat Area */}
      <div className="flex flex-1 flex-col">
        <Card className="flex flex-1 flex-col">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Bot className="h-4 w-4 text-primary" />
              AI Construction Assistant
              <Badge variant="outline" className="ml-auto text-xs">
                <Zap className="mr-1 h-3 w-3" /> GPT-4o + Claude
              </Badge>
            </CardTitle>
          </CardHeader>

          {/* Messages */}
          <CardContent className="flex-1 overflow-auto p-4">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <Bot className="mb-4 h-16 w-16 text-muted-foreground opacity-30" />
                <h3 className="text-lg font-medium">AI Construction Assistant</h3>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  I can help you draft estimates, analyze documents, flag project risks,
                  schedule tasks, and answer construction management questions.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80"
                    onClick={() => setInput('Create a new lead for a kitchen renovation inquiry')}>
                    Create a lead
                  </Badge>
                  <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80"
                    onClick={() => setInput('Summarize the latest project updates')}>
                    Summarize projects
                  </Badge>
                  <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80"
                    onClick={() => setInput('What risks should I watch for on my active projects?')}>
                    Flag risks
                  </Badge>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      'flex gap-3',
                      msg.role === 'user' ? 'justify-end' : 'justify-start',
                    )}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] rounded-lg px-4 py-2 text-sm',
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted',
                      )}
                    >
                      {msg.content ? (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      ) : (
                        <div className="flex items-center gap-2 py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-muted-foreground">Thinking...</span>
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </CardContent>

          {/* Input */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <Textarea
                placeholder="Ask the AI assistant anything..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="min-h-[44px] flex-1 resize-none"
                rows={1}
                disabled={isStreaming}
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
              >
                {isStreaming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
